import React, { useCallback, useContext, useState } from 'react';
import styled from 'styled-components';
import type { TrackingDataV2 } from '@oneaudi/audi-tracking-service';
import { Headline } from '@oneaudi/unified-web-components';
import { getClassNamesForLinks, getLinkTarget } from '../../services';
import { timeHeaderTransitionDelay, timeS, timeXXL } from '../AudiHeader/AudiHeaderStyleUtils';
import type { AudiHeaderTeaserProps } from '../../interfaces/header-components.interfaces';
import AudiHeaderTeaserTechLabel from './AudiHeaderTeaserTechLabel';
import { EventAction } from '../../utils/tracking';
import { scriptRegex } from '../../utils/string-utils';
import { ServiceContext } from '../../context/ServiceContext';
import { responsiveStyles } from '../../utils/responsiveStyles';
import type { CustomTheme } from '../../../@types/theme';
import { SUB_NAVIGATION_CLASSNAME } from '../../utils/globals';

const HeaderStyledTeaserImageContainer = styled.div<{
  isImageLoaded: boolean;
  theme: CustomTheme;
}>`
  margin-bottom: var(--one-header-space-xxxl);
  ${({ theme }): string =>
    responsiveStyles(
      {
        'max-width': {
          m: '432px',
        },
        width: {
          m: '50%',
          xl: '30vw',
        },
      },
      theme.breakpoints,
    )}
  overflow: hidden;
  width: 100%;
`;

const HeaderStyledTeaserImage = styled.img<{
  isImageLoaded: boolean;
}>`
  display: block;
  ${({ theme, isImageLoaded }): string =>
    theme.type !== 'legacy' ? 'height: 238px;' : isImageLoaded ? 'height: auto;' : 'height: 0;'};
  ${({ theme }): string => (theme.type !== 'legacy' ? 'object-fit: cover;' : '')};
  ${({ isImageLoaded }): string => {
    if (isImageLoaded) {
      return `position: relative;
        width: 100%;`;
    }
    return '';
  }}
  opacity: 1;
  padding-bottom: ${({ isImageLoaded }): string => (isImageLoaded ? '0' : '56.25%')};
  border-radius: ${({ theme }): string => (theme.type === 'legacy' ? '0' : theme.border.radius.m)};
`;

const HeaderStyledTeaserImageShadow = styled.div<{ isImageLoaded: boolean }>`
  opacity: ${({ isImageLoaded }): string => (isImageLoaded ? '1' : '0')};
  transition: opacity ${({ theme }): string => theme.easing.easingDefault}
    ${timeHeaderTransitionDelay} ${timeS};
  z-index: 1;
  ${({ theme }): string => (theme.type !== 'legacy' ? 'max-height: 238px;' : '')};

  &::after {
    /* stylelint-disable */
    background-image: linear-gradient(to bottom, transparent, transparent 70%, #000 100%);
    /* stylelint-enable */
    bottom: 0;
    content: '';
    display: block;
    height: 100%;
    opacity: 0.7;
    position: absolute;
    width: 100%;
  }
`;

const HeaderStyledTeaserImageContainerInner = styled.div`
  overflow: hidden;
  position: relative;
`;

const HeaderStyledTeaserLink = styled.a<{
  isImageLoaded: boolean;
}>`
  align-items: flex-end;
  bottom: 0;
  display: flex;
  justify-content: space-between;
  position: absolute;
  text-decoration: none;

  ${({ theme, isImageLoaded }): string =>
    theme.type === 'legacy'
      ? isImageLoaded
        ? 'transform: translateY(0);'
        : 'transform: translateY(80px);'
      : ''};
  ${({ theme }): string =>
    theme.type === 'legacy'
      ? `transition: transform ${timeXXL} ${theme.easing.easingDefault} ${timeXXL};`
      : ''};
  width: 100%;

  & > h4 {
    margin-inline-end: var(--one-header-space-s);
    margin-block-end: var(--one-header-space-xs);
    margin-inline-start: var(--one-header-space-m);
    & > span {
      margin: 0;
      ${({ theme }): string =>
        theme.type === 'legacy' ? `color: var(--one-header-color-grey-light);` : ''};
    }
  }

  & svg {
    color: var(--one-header-color-white);
    flex-shrink: 0;
    height: 48px;
    margin-bottom: var(--one-header-space-xxs);
    margin-right: var(--one-header-space-xxs);
    width: 48px;
  }
`;

const HeaderStyledTeaserWltpContainer = styled.div<{
  isImageLoaded: boolean;
}>`
  opacity: ${({ isImageLoaded }): string => (isImageLoaded ? '1' : '0')};
  padding-top: var(--one-header-space-s);
  transition: opacity ${({ theme }): string => theme.easing.easingDefault} ${timeXXL} ${timeS};
`;

const AudiHeaderTeaser: React.FC<AudiHeaderTeaserProps> = ({
  globalPosition,
  mainCategoryLabel,
  teaser,
  isSubNavOpen,
  useFootnoteReferenceServiceTextParserHook,
}) => {
  const { trackingService } = useContext(ServiceContext);
  const fallbackImage =
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR4nGP6zwAAAgcBApocMXEAAAAASUVORK5CYII=';

  const [isImageLoaded, setImageLoaded] = useState<boolean>(false);
  const [imageSource, setImageSource] = useState<string>(teaser.Image?.Src || '');

  const teaserImageLoaded = useCallback((): void => {
    if (imageSource !== fallbackImage && isSubNavOpen) {
      setImageLoaded(true);
    } else {
      setImageSource(teaser.Image.Src);
    }
  }, [isSubNavOpen, imageSource, teaser.Image?.Src]);

  const dangerousHTML = useFootnoteReferenceServiceTextParserHook(
    teaser.Link?.Text?.replace(scriptRegex, ' illegal script '),
  );

  const showTeaserWltpContainer =
    !!teaser && !!teaser.Wltp && !!teaser.Wltp.length && teaser.Wltp.length > 0;

  const urlTarget = getLinkTarget(teaser?.Link);

  const trackOnTeaserClick = () => {
    const teaserClickEvent: TrackingDataV2 = {
      event: {
        eventInfo: {
          eventAction: EventAction.INTERNAL,
          eventName: 'one header - teaser click',
        },
        attributes: {
          componentName: EventAction.COMPONENT_NAME,
          label: `Menu > ${mainCategoryLabel} > ${dangerousHTML}`,
          targetURL: teaser.Link.Url,
          clickID: '',
          elementName: 'image',
          value: '',
          pos: globalPosition,
        },
      },
    };
    trackingService?.track(teaserClickEvent);
  };

  return (
    <HeaderStyledTeaserImageContainer isImageLoaded={isImageLoaded}>
      <HeaderStyledTeaserImageContainerInner>
        <HeaderStyledTeaserImageShadow isImageLoaded={isImageLoaded}>
          <HeaderStyledTeaserImage
            alt={teaser.Image?.Alt}
            height={teaser.Image?.Height}
            isImageLoaded={isImageLoaded}
            onLoad={teaserImageLoaded}
            {...(isSubNavOpen || isImageLoaded ? { src: imageSource } : { src: fallbackImage })}
            width={teaser?.Image?.Width}
          />
        </HeaderStyledTeaserImageShadow>
        {teaser.Link.Url && (
          <HeaderStyledTeaserLink
            tabIndex={isSubNavOpen ? 0 : -1}
            className={`${getClassNamesForLinks(teaser.Link)} ${SUB_NAVIGATION_CLASSNAME}`}
            href={teaser.Link.Url}
            isImageLoaded={isImageLoaded}
            target={urlTarget}
            onClick={trackOnTeaserClick}
          >
            <Headline
              tag="h4"
              variant={{
                order: '4',
                type: 'headline',
                weight: 'normal',
              }}
            >
              <span
                dangerouslySetInnerHTML={{
                  __html: dangerousHTML,
                }}
              />
            </Headline>

            <svg height="48" viewBox="0 0 48 48" width="48" xmlns="http://www.w3.org/2000/svg">
              <path d="M19.4 32.6L28.5 23.5 19.4 14.4" fill="none" stroke="currentColor" />
            </svg>
          </HeaderStyledTeaserLink>
        )}
      </HeaderStyledTeaserImageContainerInner>
      {showTeaserWltpContainer && (
        <HeaderStyledTeaserWltpContainer isImageLoaded={isImageLoaded}>
          {teaser.Wltp?.map((Wltp) => {
            const key = `${Wltp.TechLabel}${Wltp.TechValue}`;
            const techLabelValue = `${Wltp.TechLabel} ${Wltp.TechValue}`;
            return (
              <AudiHeaderTeaserTechLabel
                key={key}
                techLabelValue={techLabelValue}
                useFootnoteReferenceServiceTextParserHook={
                  useFootnoteReferenceServiceTextParserHook
                }
              />
            );
          })}
        </HeaderStyledTeaserWltpContainer>
      )}
    </HeaderStyledTeaserImageContainer>
  );
};

export default AudiHeaderTeaser;
